<template>
  <BaseFetchData :action="loadSubscriptionPlans">
    <template #default="fetchDataProps">
      <div v-if="fetchDataProps.status.resolved" class="max-w-3xl w-full mx-auto">
        <BaseVeeForm :action="submitForm">
          <template #default="formProps">
            <BaseCard>
              <BaseCardBody clear-padding>
                <ul class="divide-y">
                  <li
                    v-for="(subscriptionPlan, subscriptionPlanIdx) of subscriptionPlans"
                    :key="subscriptionPlan.id"
                    class="p-4 sm:p-6"
                  >
                    <SubscriptionPlan
                      :vid-prefix="`subscriptionPlans.[${subscriptionPlanIdx}].`"
                      :subscription-plan="subscriptionPlan"
                      :errors="
                        filterErrorsByPrefix(
                          formProps.errors,
                          `subscriptionPlans.[${subscriptionPlanIdx}].`
                        )
                      "
                      :disabled="formProps.isPending"
                      :is-first="subscriptionPlanIdx === 0"
                      :is-last="subscriptionPlanIdx === subscriptionPlans.length - 1"
                      @update="updateSubscriptionPlan"
                      @move="moveSubscriptionPlan"
                      @remove="removeSubscriptionPlan"
                    />
                  </li>
                  <li class="p-4 sm:p-6 text-center">
                    <BaseButton
                      type="button"
                      primary
                      outline
                      :disabled="formProps.isPending"
                      @click="addSubscriptionPlan"
                      >Add plan</BaseButton
                    >
                  </li>
                </ul>
              </BaseCardBody>
              <BaseCardBody>
                <BaseButton
                  type="button"
                  primary
                  :disabled="formProps.isPending || formProps.invalid"
                  @click="submitForm"
                  >Save</BaseButton
                >
              </BaseCardBody>
            </BaseCard>
          </template>
        </BaseVeeForm>
      </div>
    </template>
  </BaseFetchData>
</template>

<script>
import { uniqueId, pickBy, startsWith } from 'lodash-es';
import { productSubscriptionPlansClient } from '@/api/clients/admin';
import SubscriptionPlan from './components/SubscriptionPlan.vue';

const defaultSubscriptionPlanData = {
  overrideProductPrice: 0,
  subscriptionPrice: '',
  subscriptionPriceDiscount: '',
  subscriptionPeriod: 'month',
  subscriptionPeriodInterval: 1,
  subscriptionLength: null,
};

const getDefaultSubscriptionPlanData = () => {
  return {
    id: uniqueId('subscription_plan_'),
    ...defaultSubscriptionPlanData,
  };
};

const getSubscriptionPlanData = (subscriptionPlan) => {
  return {
    id: subscriptionPlan.id,
    overrideProductPrice: subscriptionPlan.override_product_price ? 1 : 0,
    subscriptionPrice: subscriptionPlan.subscription_price / 100,
    subscriptionPriceDiscount: subscriptionPlan.subscription_price_discount / 100,
    subscriptionPeriod: subscriptionPlan.subscription_period,
    subscriptionPeriodInterval: subscriptionPlan.subscription_period_interval,
    subscriptionLength: subscriptionPlan.subscription_length,
  };
};

const transformToSubscriptionPlanFormData = (subscriptionPlan) => {
  const overrideProductPrice = Boolean(subscriptionPlan.overrideProductPrice);

  return {
    ...(subscriptionPlan.id.includes('subscription_plan_') ? {} : { id: subscriptionPlan.id }),
    override_product_price: overrideProductPrice,
    subscription_length: subscriptionPlan.subscriptionLength,
    subscription_period: subscriptionPlan.subscriptionPeriod,
    subscription_period_interval: subscriptionPlan.subscriptionPeriodInterval,
    ...(overrideProductPrice
      ? { subscription_price: subscriptionPlan.subscriptionPrice * 100 }
      : { subscription_price_discount: subscriptionPlan.subscriptionPriceDiscount * 100 }),
  };
};

export default {
  components: { SubscriptionPlan },
  props: {
    productId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      allowOneTimePurchase: true,
      defaultSubscriptionPlan: null,
      subscriptionPlans: [],
    };
  },
  methods: {
    filterErrorsByPrefix(errors, prefix) {
      return pickBy(errors, (value, key) => {
        return startsWith(key, prefix);
      });
    },
    loadSubscriptionPlans() {
      return productSubscriptionPlansClient.list(this.productId).then((r) => {
        this.subscriptionPlans = r.data.map(getSubscriptionPlanData);
      });
    },
    addSubscriptionPlan() {
      this.subscriptionPlans.push(getDefaultSubscriptionPlanData());
    },
    updateSubscriptionPlan({ id, attributes }) {
      const subscriptionPlanIndex = this.subscriptionPlans.findIndex(
        (subscriptionPlan) => subscriptionPlan.id === id
      );

      if (subscriptionPlanIndex === -1) {
        return;
      }

      this.subscriptionPlans.splice(subscriptionPlanIndex, 1, {
        ...this.subscriptionPlans[subscriptionPlanIndex],
        ...attributes,
      });
    },
    moveSubscriptionPlan({ id, direction }) {
      const subscriptionPlanIndex = this.subscriptionPlans.findIndex(
        (subscriptionPlan) => subscriptionPlan.id === id
      );

      if (subscriptionPlanIndex === -1) {
        return;
      }

      if (direction === 'up' && subscriptionPlanIndex === 0) {
        return;
      }

      if (direction === 'down' && subscriptionPlanIndex === this.subscriptionPlans.length - 1) {
        return;
      }

      const removedSubscriptionPlan = this.subscriptionPlans.splice(subscriptionPlanIndex, 1);

      if (direction === 'up') {
        this.subscriptionPlans.splice(subscriptionPlanIndex - 1, 0, removedSubscriptionPlan[0]);
      } else {
        this.subscriptionPlans.splice(subscriptionPlanIndex + 1, 0, removedSubscriptionPlan[0]);
      }
    },
    removeSubscriptionPlan(subscriptionPlanId) {
      const subscriptionPlanIndex = this.subscriptionPlans.findIndex(
        (subscriptionPlan) => subscriptionPlan.id === subscriptionPlanId
      );

      this.subscriptionPlans.splice(subscriptionPlanIndex, 1);
    },
    submitForm() {
      const data = {
        subscription_plans: this.subscriptionPlans.map(transformToSubscriptionPlanFormData),
      };

      return productSubscriptionPlansClient.update(this.productId, data).then((r) => {
        this.subscriptionPlans = r.data.map(getSubscriptionPlanData);
      });
    },
  },
};
</script>
